import ApiErrorBanner from '@/components/ApiErrorBanner';
import { checkStudyForDuplicates } from '@/components/Modals/AssignIdentifiers/AssignIdentifiers.utils';
import { WeightPercentageChange } from '@/components/Modals/SetupWorkflow/SetupWorkflow.model';
import Button from '@/components/UI/Button';
import { defaultPromiseErrorHandler, toNumericId } from '@/helpers';
import { _isEmpty, _isEmptyString, _isNil, _isNotBlank, _isNotEmpty, _isNumber, _notNil } from '@/littledash';
import type { AnimalApiId } from '@/model/Animal.model';
import type { PresetCalculation } from '@/model/PresetCalculation.model';
import { PresetCalculationMeasurement } from '@/model/PresetCalculationMeasurement.model';
import { State } from '@/model/State.model';
import { ApiService } from '@/support/ApiService';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';
import useAudioNotifications from '@/support/Hooks/useAudioNotifications';
import { useDevices } from '@/utils/devices/useDevices';
import { compileFormula } from '@/utils/FormulaEngine.ts';
import { ErrorMessage } from '@hookform/error-message';
import { EvalFunction } from 'mathjs';
import React, { KeyboardEventHandler, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form-latest';
import { useSelector } from 'react-redux';
import useMeasurementComparator from '../useMeasurementComparator';
import type { RenderFunctions } from '../useMeasurementComparator/useMeasurementComparator.model';
import {
  autoSwap,
  isFormDisabled,
  reversePresetDependencies,
  PercentageTypes,
  SavedDifferenceMapping,
} from './Form.utils';
import {
  CreateOrUpdateMeasurementRequest,
  FormComponentProps,
  MeasurementFormData,
  MeasurementV1,
  MeasuringField,
} from '@/components/Subjects/Show/Measurements/MeasurementWorkflow.model.tsx';
import MeasurementField from '@/components/Subjects/Show/Measurements/Form/MeasurementField.tsx';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { AlertService } from '@/utils/alerts/useAlert.ts';
import { useSelectedAnimal, useSubjects, useSavedValueDifference } from '@/utils/workflow/useWorkflow.tsx';

const createOrUpdateMeasurement = async (request: CreateOrUpdateMeasurementRequest): Promise<MeasurementV1> => {
  if (request.isUpdate) {
    const requestBody = {
      ...request.measurement,
      ...(_isNotEmpty(request.reasonForChange) ? { reason_for_change: request.reasonForChange } : null),
    };
    const updateResponse = await ApiService.call({
      endpoint: 'PATCH /api/v1/studies/{studyId}/animals/{animalId}/measurements/{measurementId}',
      path: { studyId: request.studyId, animalId: request.animalId, measurementId: request.measurementId },
      body: requestBody,
      signal: request.signal,
    });
    if (updateResponse.type === 'success') {
      return updateResponse.body;
    }
    throw updateResponse;
  }
  const createResponse = await ApiService.call({
    endpoint: 'POST /api/v1/studies/{studyId}/animals/{animalId}/measurements',
    path: { studyId: request.studyId, animalId: request.animalId },
    body: request.measurement,
    signal: request.signal,
  });
  if (createResponse.type === 'success') {
    return createResponse.body;
  }
  throw createResponse;
};

const FormComponent: React.FC<FormComponentProps> = ({
  studyId,
  preset,
  subject,
  editing,
  settings: { measured_at, measuring },
  onMeasurementSubmit,
  refetchAnimal,
  todaysMeasurement,
  disabled,
  percentChange,
  playMeasurementNoise,
  measurements,
  altIdToAssign,
  setUnsavedMeasurements,
  cursorPosition,
  cageName,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [apiError, setApiError] = useState<unknown | false>(false);

  const { newAbortController: measurementAbortController } = useAbortController();
  const state = useSelector((state: State) => state?.team);
  const formRef = useRef<HTMLFormElement | null>(null);

  // Get current subject from provider for fresh alt ID data
  const { selectedAnimal } = useSelectedAnimal();
  const { availableSubjects } = useSubjects();
  const { savedValueDifference, updateSavedValueDifference } = useSavedValueDifference();
  const providerSubject = availableSubjects?.find((s) => s.id === selectedAnimal);

  // Use provider subject for alt ID functionality, fallback to prop subject for other uses
  const subjectForAltId = providerSubject || subject;

  const measurementComparator: Record<string, RenderFunctions> = useMeasurementComparator({
    measurements: measurements ?? [],
    calculations: preset?.calculations ?? [],
    comparatorDate: subjectForAltId?.tracking_started_at,
    measuredAtDate: measured_at,
  });

  const assigningAltIdEnabled = _notNil(altIdToAssign);

  const measuringFields = useMemo(() => {
    const calculationsMap = new Map(preset.calculations.map((calc) => [calc.id, calc]));

    const reverseDependencyTree = reversePresetDependencies(preset?.dependency_tree ?? {});

    const allParentCalcInputsNotRequired = (parentIds: string[]): boolean | undefined => {
      if (parentIds.length === 0) {
        return undefined;
      }

      return parentIds.every((parentId) => {
        const parentCalculation = calculationsMap.get(parentId);
        return parentCalculation?.measurements_not_always_required === true;
      });
    };

    return measuring.reduce<MeasuringField[]>((measuringFields, measurementId) => {
      const calculation = calculationsMap.get(measurementId);
      if (_isNil(calculation)) {
        const error = new InVivoError('Calculation not found for measurement ID', {
          slug: 'measurement-calculation-mismatch',
          context: {
            measurementId,
            availableCalculationIds: preset.calculations.map((calc) => calc.id).join(', '),
          },
        });
        ExceptionHandler.captureException(error);
        return measuringFields;
      }

      const inputList = calculation.measurements?.map((measurement) => measurement.id) ?? [];
      const parentCalculationIds = reverseDependencyTree[calculation.id] ?? [];
      const parentDependencyCalculationsNotRequired = allParentCalcInputsNotRequired(parentCalculationIds);

      measuringFields.push({
        ...calculation,
        parent_dependency_calculations_not_required: parentDependencyCalculationsNotRequired,
        input_list: inputList,
      });

      return measuringFields;
    }, []);
  }, [measuring, preset]);

  const defaultValues = useMemo(() => {
    // Take passed today's measurement and create a set of default values with `measuring fields`
    const existingMeasurements = todaysMeasurement?.variables ?? {};
    return measuringFields.reduce((acc: Record<string, string>, calculation: PresetCalculation) => {
      calculation.measurements?.forEach((measurement) => {
        if (_notNil(existingMeasurements?.[measurement.id])) {
          acc[measurement.id] = `${existingMeasurements[measurement.id]}`;
        }
      });
      return acc;
    }, {});
  }, [todaysMeasurement, measuringFields]);

  const fields = useMemo(() => {
    return measuringFields.reduce<Record<string, string>>((acc: Record<string, string>, v: PresetCalculation) => {
      (v?.measurements ?? []).forEach((measurement: PresetCalculationMeasurement) => {
        acc = {
          ...acc,
          [measurement.id]: defaultValues?.[measurement.id] ?? '',
        };
      });
      return acc;
    }, {});
  }, [measuringFields, defaultValues]);

  const { playError, playNotify } = useAudioNotifications({ enabled: playMeasurementNoise ?? false });

  const formMethods = useForm<MeasurementFormData>({
    defaultValues: {
      fields: defaultValues,
      alt_id: assigningAltIdEnabled ? (subjectForAltId.alt_ids?.[altIdToAssign] ?? '') : '',
      reason_for_change: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });
  const { control, register, handleSubmit, reset, setValue, getValues, formState, trigger } = formMethods;
  const { errors, dirtyFields } = formState;

  const isDirty = !!Object.keys(dirtyFields).length;

  useEffect(() => {
    setUnsavedMeasurements?.(isDirty);
  }, [isDirty]);

  const fieldWatch: Record<string, string> = useWatch({ control, name: 'fields' });
  const { formulaList } = useMemo(() => {
    const activeCalculations = new Set([...measuring]);
    const dependencyGraph = Object.entries(preset?.dependency_tree ?? []).reduce<
      Map<string, { parents: Set<string>; children: Set<string> }>
    >((acc, [id, parents]) => {
      if (!acc.has(id)) {
        acc.set(id, { parents: new Set(parents), children: new Set<string>() });
      }
      parents.forEach((parent) => {
        if (acc.has(parent)) {
          acc.get(parent)?.children.add(id);
        } else {
          acc.set(id, { parents: new Set(), children: new Set<string>([id]) });
        }
      });
      return acc;
    }, new Map());
    const formulaList = (preset.calculations ?? [])
      .reduce<Array<{ id: string; formula: EvalFunction }>>((acc, calculation) => {
        if (activeCalculations.has(calculation.id)) {
          acc.push({ id: calculation.id, formula: compileFormula(calculation.formula) });
        }
        return acc;
      }, [])
      .sort((a, b) => {
        if (dependencyGraph.get(a?.id)?.children.has(b?.id)) {
          return -1;
        } else if (dependencyGraph.get(b?.id)?.children.has(a?.id)) {
          return 1;
        }
        return 0;
      });
    return { formulaList, dependencyGraph };
  }, [preset, measuring]);
  const variables = useMemo(
    () =>
      Object.entries(fieldWatch ?? {}).reduce<Record<string, number>>(
        (acc, [key, value]) => ({ ...acc, [key]: _isEmptyString(value) ? NaN : Number(value) }),
        {}
      ),
    [fieldWatch]
  );
  const formulas = useMemo(
    () =>
      (formulaList ?? []).reduce<Record<string, number>>((acc, { id, formula }) => {
        try {
          const result = formula.evaluate({ ...variables, ...acc });
          if (Number.isFinite(result)) {
            return { ...acc, [id]: result };
          }
        } catch (err) {
          // intentionally empty
        }
        return { ...acc, [id]: NaN };
      }, {}),
    [formulaList, variables]
  );

  const onSubmit = async ({
    fields,
    alt_id,
    reason_for_change,
  }: {
    fields?: Record<string, string>;
    alt_id?: string;
    reason_for_change?: string;
  }) => {
    playNotify();
    if (!submittingRef.current) {
      submittingRef.current = true;
      setSubmitting(true);
      setApiError(false);

      // Capture all three percentage types before they disappear
      const currentValueDifferences = measuringFields.reduce<Record<string, PercentageTypes>>((acc, field) => {
        if (_notNil(percentChange?.[field.id])) {
          const comparator = measurementComparator?.[field.id];
          const fieldValue = fieldWatch?.[field.id];
          const formulaValue = formulas?.[field.id];

          // Get current value (either input or calculated)
          let currentValue: number | null = null;
          if (_isNotBlank(fieldValue)) {
            const numericValue = Number(fieldValue);
            if (!isNaN(numericValue) && isFinite(numericValue)) {
              currentValue = numericValue;
            }
          } else if (_isNumber(formulaValue) && isFinite(formulaValue)) {
            currentValue = formulaValue;
          }

          if (_isNumber(currentValue) && comparator) {
            const percentageTypes: PercentageTypes = {};

            const fromFirstPercent = comparator.fromFirst(currentValue);
            if (_isNumber(fromFirstPercent)) {
              percentageTypes.fromFirst = fromFirstPercent;
            }

            const fromLatestPercent = comparator.fromLatest(currentValue);
            if (_isNumber(fromLatestPercent)) {
              percentageTypes.fromLatest = fromLatestPercent;
            }

            const fromMaximumPercent = comparator.fromMaximum(currentValue);
            if (_isNumber(fromMaximumPercent)) {
              percentageTypes.fromMaximum = fromMaximumPercent;
            }

            const trackingDatePercent = comparator.comparatorDate(currentValue);
            if (_isNumber(trackingDatePercent)) {
              percentageTypes.trackingDate = trackingDatePercent;
            }

            if (Object.keys(percentageTypes).length > 0) {
              acc[field.id] = percentageTypes;
            }
          }
        }
        return acc;
      }, {});

      const activeVariableIds = new Set(measuringFields.flatMap((field) => field.measurements?.map((m) => m.id) ?? []));
      const payload = autoSwap(preset.calculations, fields ?? {});

      const variables = Object.entries(payload).reduce<Array<{ name: string; value: `${number}` | null }>>(
        (acc, [name, value]) => {
          if (!activeVariableIds.has(name)) {
            return acc;
          }
          if ((defaultValues?.[name] ?? '') === value) {
            return acc;
          }
          if (_isEmptyString(value ?? '')) {
            if (editing) {
              return [...acc, { name, value: null }];
            }
            return acc;
          }
          return [...acc, { name, value: `${value as number}` }];
        },
        []
      );
      let measurementCallbackValue: MeasurementV1 | null = null;
      let newFieldsValue = {};
      let newAltIdValue = '';

      if (_isNotEmpty(variables)) {
        const response = await processMeasurement(variables, reason_for_change);
        if (_notNil(response?.measurementSubmitResponse)) {
          measurementCallbackValue = response.measurementSubmitResponse;
        }
        if (_isNotEmpty(response?.updatedFields)) {
          newFieldsValue = response.updatedFields;
        }
      }

      if (assigningAltIdEnabled && altIdToAssign) {
        const existingAltId = subjectForAltId.alt_ids?.[altIdToAssign];
        const altIdInputDisabled = _notNil(existingAltId);
        const altIdUnchanged = (alt_id ?? '') === (existingAltId ?? '');

        if (!altIdInputDisabled && !altIdUnchanged) {
          const newAltId = await processAltId(alt_id);
          newAltIdValue = newAltId ?? '';
          await refetchAnimal?.();
        } else {
          newAltIdValue = existingAltId ?? alt_id ?? '';
        }
      }
      await onMeasurementSubmit(measurementCallbackValue, editing);

      // Store captured value differences for new measurements
      if (Object.keys(currentValueDifferences).length > 0) {
        updateSavedValueDifference(subject.id, currentValueDifferences);
      }

      reset({ fields: newFieldsValue, alt_id: newAltIdValue });
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  const processMeasurement = async (
    variables: Array<{ name: string; value: `${number}` | null }>,
    reason_for_change?: string
  ): Promise<{ measurementSubmitResponse: MeasurementV1 | null; updatedFields: Record<string, string> } | void> => {
    try {
      if (_isNotEmpty(Object.values(variables))) {
        const measurementId = todaysMeasurement?.api_id;
        const measurementSubmitResponse = await createOrUpdateMeasurement({
          ...(_notNil(measurementId) ? { isUpdate: true, measurementId } : { isUpdate: false }),
          studyId,
          animalId: subjectForAltId.api_id as AnimalApiId,
          measurement: { variables, measured_at },
          signal: measurementAbortController().signal,
          reasonForChange: reason_for_change,
        });

        const alerts = measurementSubmitResponse?.subject_alerts ?? [];
        if (_isNotEmpty(alerts)) {
          AlertService.createAlerts({
            alerts,
            calculations: preset.calculations,
            animalName: subjectForAltId.name ?? '',
            studyApiId: studyId,
            animalApiId: subjectForAltId.api_id as AnimalApiId,
            cageName,
          });
        }
        const updatedFields = Object.keys(fields).reduce<Record<string, string>>(
          (acc, field) => ({
            ...acc,
            [field]: measurementSubmitResponse?.variables?.[field] ?? acc[field],
          }),
          {}
        );
        return { measurementSubmitResponse: measurementSubmitResponse ?? null, updatedFields };
      }
    } catch (apiError) {
      if (notAborted(apiError)) {
        setApiError(apiError);
      }
    }
  };

  const processAltId = async (alt_id?: string) => {
    try {
      if (assigningAltIdEnabled && _notNil(alt_id)) {
        const payload = {
          animals: [
            {
              alt_ids: { ...subjectForAltId.alt_ids, [altIdToAssign]: alt_id },
              id: toNumericId(subjectForAltId.id),
              name: subjectForAltId.name,
            },
          ],
        };
        const response = await ApiService.call({
          endpoint: 'PATCH /api/v1/animals/identifiers',
          body: payload,
          options: { onError: { throw: true, toast: false, slug: 'measurements-assign-alt-id' } },
        });
        if (response.type === 'success') {
          return response.body?.data?.[0]?.alt_ids?.[altIdToAssign];
        }
      }
    } catch (ex) {
      if (notAborted(ex)) {
        setApiError(ex);
      }
    }
  };

  const handleKeyPress: KeyboardEventHandler<HTMLInputElement> = (event) => {
    // #9 = Tab / #13 = Enter
    if (event.keyCode === 13 || event.keyCode === 9) {
      event.preventDefault();
      playNotify();
      const index = Array.prototype.indexOf.call(formRef.current, event.target);
      if (event.shiftKey) {
        (formRef.current?.elements?.[index ? index - 1 : 0] as HTMLFormElement)?.focus();
      } else {
        const allFields = getValues();
        const fields = { ...allFields.fields };
        if (assigningAltIdEnabled) {
          fields.alt_id = allFields.alt_id;
        }
        if (_notNil(fields)) {
          const measurementsComplete = Object.values(fields).every((field) => _isNotEmpty(field));
          const assignIdsSet = index + 1 === Object.keys(fields).length + (_notNil(altIdToAssign) ? 1 : 0);

          // Dirty fix for alt id not being a measurement field
          if ((measurementsComplete || assignIdsSet) && !submittingRef.current) {
            handleSubmit(onSubmit)().catch(defaultPromiseErrorHandler);
          } else {
            (formRef.current?.[index + 1] as HTMLFormElement)?.focus();
          }
        }
      }
    }
  };

  const handleDifference = (measurementKey: string): number | null => {
    // Use saved value difference only if current field value matches the saved measurement value
    const savedMeasurementValue = todaysMeasurement?.variables?.[measurementKey];
    const currentValue = fieldWatch?.[measurementKey] || formulas?.[measurementKey];
    const valuesMatch = String(currentValue) === String(savedMeasurementValue ?? '');

    const savedPercentages = savedValueDifference?.[subject.id]?.[measurementKey];
    const currentTrackingMode = percentChange?.[measurementKey];

    if (savedPercentages && valuesMatch && currentTrackingMode) {
      // Return the appropriate saved percentage based on current tracking mode
      const savedDifference = savedPercentages[SavedDifferenceMapping[currentTrackingMode]];

      if (_isNumber(savedDifference)) {
        return savedDifference;
      }
    }

    // Use live calculation when values don't match or no saved value difference
    const comparator = measurementComparator?.[measurementKey];
    const fieldValue = _notNil(fieldWatch?.[measurementKey]) ? Number(fieldWatch[measurementKey]) : null;
    const formulaValue = _isNumber(formulas?.[measurementKey]) ? formulas[measurementKey] : null;
    const value = fieldValue ?? formulaValue;

    const mappingMethod = SavedDifferenceMapping[currentTrackingMode as WeightPercentageChange];
    return _notNil(mappingMethod)
      ? mappingMethod === 'trackingDate'
        ? (comparator?.comparatorDate(value) ?? null)
        : (comparator?.[mappingMethod](value) ?? null)
      : null;
  };

  const showDifference = (calcId: string): boolean => {
    // Must have a current value to show percentage with
    const hasCurrentValue = _isNotBlank(fieldWatch?.[calcId]) || _isNumber(formulas?.[calcId]);
    if (!hasCurrentValue) {
      return false;
    }

    // Check if current value matches saved measurement value
    const savedValue = todaysMeasurement?.variables?.[calcId];
    const currentValue = fieldWatch?.[calcId] || formulas?.[calcId];
    const valuesMatch = String(currentValue) === String(savedValue ?? '');

    // Show saved value difference only if values match AND we have a saved value difference for current mode
    const savedPercentages = savedValueDifference?.[subject.id]?.[calcId];
    const currentTrackingMode = percentChange?.[calcId];

    if (valuesMatch && savedPercentages && currentTrackingMode) {
      const mappedKey = SavedDifferenceMapping[currentTrackingMode];
      const relevantSavedDifference = _notNil(mappedKey) ? savedPercentages[mappedKey] : undefined;

      if (_isNumber(relevantSavedDifference)) {
        return true;
      }
    }

    if (_notNil(currentTrackingMode)) {
      const calculatedDifference = handleDifference(calcId);
      return _isNumber(calculatedDifference);
    }

    return false;
  };

  const allMeasurementFieldsFilled = Object.values(fields).every((f) => f !== '');
  const altIdFieldFilled = altIdToAssign ? _isNotBlank(subjectForAltId.alt_ids?.[altIdToAssign]) : false;

  const submitDisabled = isFormDisabled({
    submitting,
    editing,
    altIdFieldFilled,
    allMeasurementFieldsFilled,
    assignAltId: _notNil(altIdToAssign),
  });

  const { errors: stateErrors } = useFormState(formMethods);

  const onNewReading = async (response: string) => {
    if (assigningAltIdEnabled && !altIdFieldFilled) {
      setValue('alt_id', response);
      await trigger('alt_id');
      if (stateErrors?.alt_id) {
        playError();
      } else {
        playNotify();
      }
      const isReadingComplete = !preset.calculations.some((calculation) => {
        return calculation.measurements?.some((measurement) => {
          if (_notNil(measurement.id)) {
            return _isEmptyString(getValues(`fields.${measurement.id}`));
          }
          return false;
        });
      });

      nextReading(true);

      if (isReadingComplete) {
        handleSubmit(onSubmit)().catch(defaultPromiseErrorHandler);
      }
    }
  };
  const {
    nextReading,
    resetDeviceOrder,
    searchBarTriggered,
    clearSearchBarTrigger,
    applyDelayedSearchReading,
    setSearchInterceptCondition,
  } = useDevices({
    targetId: 'assign-id',
    onNewReading,
  });

  useEffect(() => {
    resetDeviceOrder(preset.id);
    clearSearchBarTrigger();
  }, [preset.id]);

  // Set up intercept condition on mount and clear on unmount
  useEffect(() => {
    setSearchInterceptCondition((targetId: string, value: string) => {
      return targetId === 'focus-search';
    });

    // Cleanup: Remove intercept condition when FormComponent unmounts
    return () => {
      setSearchInterceptCondition(() => false);
    };
  }, []);

  // React to search bar readings for auto-submission
  useEffect(() => {
    if (searchBarTriggered.targetId && searchBarTriggered.value && !submittingRef.current) {
      // Store the search reading details before clearing
      const searchTargetId = searchBarTriggered.targetId;
      const searchValue = searchBarTriggered.value;

      // Clear the trigger first to prevent re-triggering
      clearSearchBarTrigger();

      // Check if form has unsaved changes - if not, just apply the reading without submitting
      if (_isEmpty(fieldWatch) || Object.values(fieldWatch).every((v) => _isEmptyString(v)) || !isDirty) {
        applyDelayedSearchReading(searchTargetId, searchValue);
      } else {
        // Form has unsaved values, submit first then apply the reading
        handleSubmit(onSubmit)().then(() => {
          if (_isEmpty(errors)) {
            applyDelayedSearchReading(searchTargetId, searchValue);
          }
        });
      }
    }
  }, [searchBarTriggered.timestamp, submitting]);

  const rules: { required?: string; validate?: (value: string) => Promise<boolean | string> } = {};
  if (assigningAltIdEnabled && altIdToAssign === 'tag' && state?.features?.assign_identifiers_tag_unique === true) {
    rules.required = 'This field is required';
    rules.validate = async (value) => {
      try {
        if (_notNil(subjectForAltId.api_id)) {
          const existingAltId = subjectForAltId.alt_ids?.tag;
          if ((value ?? '') === (existingAltId ?? '')) {
            return true;
          }
          const result = await checkStudyForDuplicates(value, 'tag', studyId, subjectForAltId.api_id);
          return result ? true : 'This identifier is already in use';
        }
        return 'Animal not found';
      } catch (error) {
        return 'An error occurred';
      }
    };
  }
  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit, playError)}>
      <>
        {apiError ? (
          <ApiErrorBanner
            className="mw6 mb4"
            error={apiError}
            title="There was an error with your submission"
            text="Please correct the form errors below and try again. If this error persists try again later or contact support."
          />
        ) : null}
        {assigningAltIdEnabled ? (
          <div className="pb3 mt2 bb b--moon-gray">
            <label>{altIdToAssign.charAt(0).toUpperCase() + altIdToAssign.slice(1)}</label>
            <input
              {...register('alt_id', rules)}
              type="text"
              autoComplete="off"
              autoFocus
              onFocus={({ target }) => target.select()}
              disabled={_notNil(subjectForAltId.alt_ids?.[altIdToAssign])}
              onKeyDown={handleKeyPress}
              className={`${errors?.alt_id ? 'input__error' : ''}`}
              style={{ marginBottom: 0 }}
              data-testid={`workflow-measurement-fields-${altIdToAssign}`}
            />
            <ErrorMessage
              errors={errors}
              name="alt_id"
              render={({ message }) => (
                <p className="f6 red db lh-copy pt1" data-testid={`workflow-measurement-fields-error-${altIdToAssign}`}>
                  {message}
                </p>
              )}
            />
          </div>
        ) : null}
        {measuringFields.map((calc, index) => (
          <MeasurementField
            key={calc.id}
            index={index}
            calc={calc}
            editing={editing}
            defaultValues={defaultValues}
            submitting={submitting}
            disabled={disabled}
            handleKeyPress={handleKeyPress}
            formulas={formulas}
            showDifference={showDifference}
            handleDifference={handleDifference}
            assignAltId={_notNil(altIdToAssign)}
            formMethods={formMethods}
            fieldWatch={fieldWatch}
            onSubmit={onSubmit}
            calculations={preset.calculations}
            playErrorSound={playError}
            playNotifySound={playNotify}
            allMeasurementFieldsFilled={allMeasurementFieldsFilled}
            cursorPosition={cursorPosition}
          />
        ))}
        <div className="pt3">
          <Button submit disabled={submitDisabled} testId="workflow-measurement-save">
            Save
          </Button>
        </div>
      </>
    </form>
  );
};

export default FormComponent;
